/**
 * AppSync Client Provider for Web App
 * Similar to iOS AppSyncClientProvider
 * Supports both Cognito and API Key authentication
 */

import { fetchAuthSession } from "aws-amplify/auth";
import { config } from "../config/environment";

class AppSyncClientProvider {
  private static instance: AppSyncClientProvider;
  private graphqlUrl: string;
  private apiKey: string;

  private constructor() {
    this.graphqlUrl = config.GRAPHQL_API_URL;
    this.apiKey = config.GRAPHQL_API_KEY || '';
  }

  static getInstance(): AppSyncClientProvider {
    if (!AppSyncClientProvider.instance) {
      AppSyncClientProvider.instance = new AppSyncClientProvider();
    }
    return AppSyncClientProvider.instance;
  }

  /**
   * Get authentication token for AppSync requests
   */
  async getAuthToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString() || null;
      
      if (!token) {
        console.error("No ID token found in session");
        return null;
      }
      
      return token;
    } catch (error) {
      console.error("Error getting auth token:", error);
      return null;
    }
  }

  /**
   * Get GraphQL endpoint URL
   */
  getGraphQLUrl(): string {
    return this.graphqlUrl;
  }

  /**
   * Get API Key for public operations
   */
  getApiKey(): string {
    return this.apiKey;
  }

  /**
   * Execute a GraphQL query or mutation
   * @param query GraphQL query string
   * @param variables Query variables
   * @param useApiKey If true, use API key auth instead of Cognito token (for public operations)
   */
  async execute<T = any>(
    query: string,
    variables?: Record<string, any>,
    useApiKey: boolean = false
  ): Promise<{ data?: T; errors?: any[] }> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (useApiKey) {
      // Use API key for public operations (parent booking)
      if (!this.apiKey) {
        throw new Error("No API key available for public operation");
      }
      headers["x-api-key"] = this.apiKey;
    } else {
      // Use Cognito token for authenticated operations
      const token = await this.getAuthToken();
      
      if (!token) {
        throw new Error("No authentication token available");
      }
      headers["Authorization"] = token;
    }

    const response = await fetch(this.graphqlUrl, {
      method: "POST",
      headers,
      body: JSON.stringify({
        query,
        variables: variables || {},
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`GraphQL request failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const result = await response.json();

    if (result.errors) {
      console.error("GraphQL errors:", result.errors);
      throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
    }

    return result;
  }
}

export const appSyncClient = AppSyncClientProvider.getInstance();

